// Часовете, в които формата за резервации приема НАЧАЛО — от работното време
// на деня от седмицата (Конфигурация → Работно време, `lib/workingHours.ts`).
// Слотовете са през 15 мин: от отварянето до последния слот ПРЕДИ
// затварянето (10:00–23:00 → 10:00…22:45).
//
// СМЯНА е работният ден от отварянето до затварянето му — и когато
// затварянето е на СЛЕДВАЩАТА дата (петък 12:00–06:00 = петък 12:00…23:45 +
// събота 00:00…05:45). Формата и „Таблица на резервациите" показват цялата
// смяна наведнъж; датата на резервацията остава календарната дата на
// началото ѝ (BE `start_at`) — час след полунощ е на следващата дата. При
// работа без полунощ смяната е самият календарен ден.
// Почивен ден няма своя смяна. Ненастроено работно време → досегашните
// 11:00–22:45 за всеки ден.
//
// Само формата и разписанието; BE не проверява часа и по-старите резервации
// в други часове остават валидни.
// Правилата са обяснени с примери на екрана „Работно време" (преводите
// `workingHours.guide` — раздела `reservations`) — смениш ли ги тук, смени и текста там.

import type { WorkingDay } from "../../../api/configuration";
import { crossesMidnight, getWorkingHours, minutesOfTime, workingDayOf } from "../../../lib/workingHours";
import { addDays, minuteOfDay, parseDateKey, toDateKey } from "./dayTime";

export const SLOT_MINUTES = 15;
const DAY_MINUTES = 24 * 60;
const DEFAULT_FIRST_SLOT = 11 * 60;
const DEFAULT_LAST_SLOT = 22 * 60 + 45;
const LAST_SLOT_OF_DAY = DAY_MINUTES - SLOT_MINUTES;
// Прозорецът на разписанието в почивен ден и при ненастроено работно време —
// досегашният 11:00–24:00.
const DEFAULT_WINDOW = { startHour: 11, endHour: 24 };

/** Отрязък от слотове за начало (минута от деня, включително). */
export interface SlotRange {
  firstSlot: number;
  lastSlot: number;
}

/** Час от смяната и календарната дата, на която е (след полунощ — следващата). */
export interface ShiftHour {
  hour: number;
  dateKey: string;
}

const clamp = (minute: number, range: SlotRange) => Math.min(range.lastSlot, Math.max(range.firstSlot, minute));

const workingDayOfDate = (dateKey: string): WorkingDay | null => workingDayOf(parseDateKey(dateKey).getDay());

/** Слотовете от СОБСТВЕНАТА смяна на датата (от отварянето ѝ до полунощ); null — почивен ден. */
const ownRangeOf = (dateKey: string): SlotRange | null => {
  if (!getWorkingHours()) return { firstSlot: DEFAULT_FIRST_SLOT, lastSlot: DEFAULT_LAST_SLOT };
  const day = workingDayOfDate(dateKey);
  if (!day || day.closed) return null;
  return {
    firstSlot: minutesOfTime(day.opensAt),
    lastSlot: crossesMidnight(day) ? LAST_SLOT_OF_DAY : minutesOfTime(day.closesAt) - SLOT_MINUTES,
  };
};

/** Затварянето на смяната от предишния ден на ТАЗИ дата (работи след полунощ); иначе null. */
const previousNightCloseOf = (dateKey: string): number | null => {
  if (!getWorkingHours()) return null;
  const previous = workingDayOfDate(addDays(dateKey, -1));
  return previous && crossesMidnight(previous) ? minutesOfTime(previous.closesAt) : null;
};

/** Слотовете на датата от смяната на ПРЕДИШНИЯ ден (00:00 … 15 мин преди затварянето ѝ); null — няма. */
export const previousNightRangeOf = (dateKey: string): SlotRange | null => {
  const close = previousNightCloseOf(dateKey);
  return close !== null && close >= SLOT_MINUTES ? { firstSlot: 0, lastSlot: close - SLOT_MINUTES } : null;
};

/**
 * Всички слотове за начало на датата, по реда на деня: тези от смяната на
 * предишния ден и тези от собствената ѝ смяна. BE не позволява предишната
 * смяна да стига до отварянето, затова двата отрязъка не се застъпват.
 */
export const slotRangesOf = (dateKey: string): SlotRange[] =>
  [previousNightRangeOf(dateKey), ownRangeOf(dateKey)].filter((range): range is SlotRange => range !== null);

/** Денят има собствена смяна (не е почивен). */
export const hasOwnHours = (dateKey: string): boolean => ownRangeOf(dateKey) !== null;

/** Часът на датата е в работното време (на която и да е смяна). */
export const isSlotInHours = (dateKey: string, minute: number): boolean =>
  slotRangesOf(dateKey).some((range) => minute >= range.firstSlot && minute <= range.lastSlot);

/** Първият слот в/след `minute` на датата; null — няма такъв. */
export const firstSlotFrom = (dateKey: string, minute: number): number | null => {
  for (const range of slotRangesOf(dateKey)) {
    if (minute <= range.lastSlot) return Math.max(range.firstSlot, minute);
  }
  return null;
};

/** Последният слот на датата; null — няма слотове. */
export const lastSlotOf = (dateKey: string): number | null => {
  const ranges = slotRangesOf(dateKey);
  return ranges.length > 0 ? ranges[ranges.length - 1].lastSlot : null;
};

/**
 * Смяната (работната дата), към която принадлежи часът на датата: след
 * полунощ, до затварянето на смяната от предишния ден — предишният ден.
 */
export const shiftDateOf = (dateKey: string, minute: number): string => {
  const close = previousNightCloseOf(dateKey);
  return close !== null && minute < close ? addDays(dateKey, -1) : dateKey;
};

/** Смяната на момента — „днес" за таблицата: в 01:30 след петък до 06:00 е петък. */
export const shiftDateOfMoment = (moment: number): string =>
  shiftDateOf(toDateKey(new Date(moment)), minuteOfDay(moment));

/** Смяната на часа има работно време (почивен ден — не). */
export const isShiftOpen = (dateKey: string, minute: number): boolean => hasOwnHours(shiftDateOf(dateKey, minute));

/**
 * Часът, приведен в работното време на СВОЯТА смяна: вътре — без промяна;
 * преди отварянето — отварянето, след последния слот — последният. Почивен
 * ден — без промяна.
 */
export const clampToShift = (dateKey: string, minute: number): number => {
  const night = previousNightRangeOf(dateKey);
  if (night && shiftDateOf(dateKey, minute) !== dateKey) return clamp(minute, night);
  const own = ownRangeOf(dateKey);
  return own ? clamp(minute, own) : minute;
};

/**
 * Часът при избор на ДЕН (плочката): в собствената смяна на деня, така че
 * избраната плочка остава избрана — час след полунощ от друга смяна става
 * отварянето. Почивен ден → 19:00 (в собствената му част, формата казва
 * „Почивен ден").
 */
export const ownMinuteOf = (dateKey: string, minute: number, eveningSlot: number): number => {
  const own = ownRangeOf(dateKey);
  return own ? clamp(minute, own) : eveningSlot;
};

/**
 * Часовете на смяната в нейния ред: собствените (от отварянето), после тези
 * след полунощ — на следващата дата. [] — почивен ден.
 */
export const shiftHoursOf = (shiftDateKey: string): ShiftHour[] => {
  const own = ownRangeOf(shiftDateKey);
  if (!own) return [];
  const hoursOf = (range: SlotRange, dateKey: string) =>
    Array.from(
      { length: Math.floor(range.lastSlot / 60) - Math.floor(range.firstSlot / 60) + 1 },
      (_, index) => ({ hour: Math.floor(range.firstSlot / 60) + index, dateKey }),
    );
  const nextDateKey = addDays(shiftDateKey, 1);
  const night = previousNightRangeOf(nextDateKey);
  return [...hoursOf(own, shiftDateKey), ...(night ? hoursOf(night, nextDateKey) : [])];
};

/**
 * Часовете на „Таблица на резервациите" за смяната: от часа на отварянето до
 * часа на затварянето (закръглен нагоре). След полунощ прозорецът минава
 * през 24 — петък 12:00–06:00 е 12…30, т.е. до 06:00 в събота. Почивен ден
 * или ненастроено работно време — 11:00–24:00.
 */
export const scheduleWindowOf = (dateKey: string): { startHour: number; endHour: number } => {
  if (!getWorkingHours()) return DEFAULT_WINDOW;
  const day = workingDayOfDate(dateKey);
  if (!day || day.closed) return DEFAULT_WINDOW;
  const closeHour = Math.ceil(minutesOfTime(day.closesAt) / 60);
  return {
    startHour: Math.floor(minutesOfTime(day.opensAt) / 60),
    endHour: crossesMidnight(day) ? 24 + closeHour : closeHour,
  };
};
